import React, { useState, useEffect, useCallback } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import { IMG } from "../data/site";
import { ytThumbnail } from "../data/youtube";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { CalendarDays, Loader2, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { fetchEventById, fetchEventsPage, fetchLatestYoutubeVideos } from "../api";

const PAGE_SIZE = 12;
const VIDEO_COUNT = 8;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

const Gallery = () => {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageMeta, setPageMeta] = useState({ index: 0, size: PAGE_SIZE, total: 0 });
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [videos, setVideos] = useState([]);
  const open = !!selectedEvent;
  const images = eventDetail?.images?.length ? eventDetail.images : selectedEvent?.thumbnail ? [selectedEvent.thumbnail] : [];
  const current = open && images.length ? images[lightboxIndex] : null;
  const totalPages = Math.max(1, Math.ceil((pageMeta.total || 0) / PAGE_SIZE));

  useEffect(() => {
    let alive = true;

    const loadGallery = async () => {
      setGalleryLoading(true);
      try {
        const result = await fetchEventsPage({ size: PAGE_SIZE, index: pageIndex });
        if (!alive) return;
        setEvents(result.events);
        setPageMeta(result.page);
        setGalleryError("");
      } catch (error) {
        if (!alive) return;
        setGalleryError("Unable to load gallery right now.");
      } finally {
        if (alive) {
          setGalleryLoading(false);
        }
      }
    };

    loadGallery();

    return () => { alive = false; };
  }, [pageIndex]);

  useEffect(() => {
    let alive = true;

    const loadVideos = async () => {
      try {
        const latestVideos = await fetchLatestYoutubeVideos({ maxResults: VIDEO_COUNT });
        if (!alive || !latestVideos.length) return;
        setVideos(latestVideos);
      } catch (error) {
        if (!alive) return;
      }
    };

    loadVideos();

    return () => { alive = false; };
  }, []);

  const openEventGallery = async (event) => {
    setSelectedEvent(event);
    setEventDetail(null);
    setDetailError("");
    setDetailLoading(true);
    setLightboxIndex(0);

    try {
      const detail = await fetchEventById(event.id, { metadata: event });
      setEventDetail(detail);
      setLightboxIndex(0);
    } catch (error) {
      setDetailError("Unable to load images for this event.");
    } finally {
      setDetailLoading(false);
    }
  };

  const showPrev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setLightboxIndex((i) => (i - 1 + images.length) % images.length);
    },
    [images.length]
  );
  const showNext = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setLightboxIndex((i) => (i + 1) % images.length);
    },
    [images.length]
  );

  useEffect(() => {
    if (!open || images.length < 2) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
      else if (e.key === "Escape") setSelectedEvent(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, open, showPrev, showNext]);

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("gallery.hero.eyebrow")}
        title={t("gallery.hero.title")}
        subtitle={t("gallery.hero.subtitle")}
        image={IMG.processional}
      />

      {/* Event Gallery */}
      <section className="py-16 sm:py-20" data-testid="gallery-collage">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          {galleryLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" aria-live="polite">
              {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <div key={index} className="aspect-square rounded-2xl border border-[#D4AF37]/25 bg-[#F2E7D5] animate-pulse" />
              ))}
            </div>
          )}
          {!galleryLoading && galleryError && (
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0] px-5 py-4 text-sm text-[#3B2A1A]/80">
              {galleryError}
            </div>
          )}
          {!galleryLoading && !galleryError && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {events.map((event, i) => (
                  <button
                    key={event.id}
                    onClick={() => openEventGallery(event)}
                    className="group relative aspect-square overflow-hidden rounded-2xl border border-[#D4AF37]/25 card-lift bg-[#F2E7D5]"
                    data-testid={`gallery-item-${i}`}
                    aria-label={`Open event gallery: ${event.title}`}
                  >
                    <img
                      src={event.thumbnail || IMG.processional}
                      alt={event.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3B2A1A]/85 via-[#3B2A1A]/10 to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-4">
                      <div>
                        <div className="inline-flex items-center gap-1.5 text-[#D4AF37] text-[10px] font-semibold mb-1">
                          <CalendarDays className="w-3 h-3" /> {formatDate(event.date)}
                        </div>
                        <div className="text-[#FBF7F0] text-sm font-serif-display line-clamp-2">{event.title}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-10 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setPageIndex((page) => Math.max(0, page - 1))}
                  disabled={pageIndex === 0}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm font-semibold text-[#3B2A1A] disabled:opacity-45 disabled:cursor-not-allowed hover:bg-[#F2E7D5] transition"
                  data-testid="gallery-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <div className="text-sm text-[#3B2A1A]/75 min-w-[92px] text-center" data-testid="gallery-page-count">
                  Page {pageIndex + 1} / {totalPages}
                </div>
                <button
                  type="button"
                  onClick={() => setPageIndex((page) => Math.min(totalPages - 1, page + 1))}
                  disabled={pageIndex >= totalPages - 1}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm font-semibold text-[#3B2A1A] disabled:opacity-45 disabled:cursor-not-allowed hover:bg-[#F2E7D5] transition"
                  data-testid="gallery-next-page"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <SectionDivider variant="mandala" />

      {/* Videos */}
      <section className="py-16 bg-sand-texture" data-testid="gallery-videos">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={t("gallery.videos.eyebrow")} title={t("gallery.videos.title")} subtitle={t("gallery.videos.subtitle")} align="center" />
          {videos.length > 0 ? (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {videos.map((v) => (
                <a
                  key={v.id}
                  href={`https://www.youtube.com/watch?v=${v.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative rounded-2xl overflow-hidden border border-[#D4AF37]/25 card-lift cursor-pointer bg-[#FBF7F0] flex flex-col"
                  data-testid={`video-${v.id}`}
                >
                  <div className="aspect-video overflow-hidden relative">
                    <img src={ytThumbnail(v.id)} alt={v.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-[#3B2A1A]/30 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-[#3B2A1A] ml-0.5" fill="#3B2A1A" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-serif-display text-sm text-[#3B2A1A] line-clamp-2 leading-snug">{v.title}</div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="mt-14 rounded-2xl border border-[#D4AF37]/25 bg-[#FBF7F0] px-6 py-8 text-center text-sm text-[#3B2A1A]/75">
              Latest videos are not available right now.
            </div>
          )}
        </div>
      </section>

      {/* Lightbox with prev/next */}
      <Dialog open={open} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent
          className="max-w-5xl bg-transparent border-0 p-0 shadow-none"
          aria-describedby={undefined}
          data-testid="lightbox"
        >
          {selectedEvent && (
            <div className="relative flex min-h-[320px] items-center justify-center">
              <DialogTitle className="sr-only">{selectedEvent.title}</DialogTitle>

              {detailLoading && (
                <div className="rounded-2xl bg-[#FBF7F0] border border-[#D4AF37]/30 p-8 flex items-center gap-3 text-[#3B2A1A] shadow-xl">
                  <Loader2 className="w-5 h-5 animate-spin text-[#B65A14]" />
                  <span className="text-sm font-semibold">Loading images...</span>
                </div>
              )}

              {!detailLoading && detailError && (
                <div className="rounded-2xl bg-[#FBF7F0] border border-[#D4AF37]/30 p-8 text-center shadow-xl">
                  <div className="font-serif-display text-2xl text-[#3B2A1A]">{selectedEvent.title}</div>
                  <p className="mt-3 text-sm text-[#3B2A1A]/75">{detailError}</p>
                </div>
              )}

              {!detailLoading && !detailError && current && (
                <>
                  {images.length > 1 && (
                    <button
                      onClick={showPrev}
                      aria-label="Previous image"
                      className="absolute left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FBF7F0]/90 hover:bg-[#FBF7F0] text-[#3B2A1A] flex items-center justify-center shadow-lg transition"
                      data-testid="lightbox-prev"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}

                  <img
                    src={current}
                    alt={`${selectedEvent.title} ${lightboxIndex + 1}`}
                    className="w-full h-auto max-h-[82vh] object-contain rounded-xl select-none"
                    data-testid="lightbox-image"
                  />

                  {images.length > 1 && (
                    <button
                      onClick={showNext}
                      aria-label="Next image"
                      className="absolute right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FBF7F0]/90 hover:bg-[#FBF7F0] text-[#3B2A1A] flex items-center justify-center shadow-lg transition"
                      data-testid="lightbox-next"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}

                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#3B2A1A]/90 to-transparent p-6 rounded-b-xl">
                    <div className="flex items-center justify-between gap-5">
                      <div>
                        <div className="text-[#FBF7F0] font-serif-display text-lg line-clamp-1" data-testid="lightbox-caption">
                          {eventDetail?.title || selectedEvent.title}
                        </div>
                        {eventDetail?.description && (
                          <p className="mt-1 text-[#F2E7D5]/85 text-xs line-clamp-2">{eventDetail.description}</p>
                        )}
                      </div>
                      <div className="text-[#FBF7F0]/70 text-xs tracking-widest uppercase shrink-0" data-testid="lightbox-count">
                        {lightboxIndex + 1} / {images.length}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </PageLayout>
  );
};

export default Gallery;
